import { BaseView } from "./base-view";
import { Navigation } from "../navigation";
import { PageContext } from "../page-context";
import { Employee } from "../models/Employee";
import { AssignmentDemand } from "../models/AssignmentDemand";
import { buildRouteParamsString, closeModal, createEmployeePhotoElement, createNameIconElementWith2Initials, showModal, showToast } from "../common";
import { CellComponent, ColumnDefinition, TabulatorFull } from "tabulator-tables";
import { Language } from "../models/Language";

export class ResourceDetailView extends BaseView {
    /** @type {Employee} */
    data: Employee;

    /** @type {AssignmentDemand} */
    assignment!: AssignmentDemand;

    static title = "Resource details";
    static icon = "bi bi-person-bounding-box";
    seniorities!: string[];

    /**
   * constructor
   * @param {PageContext} context - The context of the page.
   * @param {string} [container=".main"] - The container to render the view in.
   */
    constructor(context: PageContext, container = ".main") {
        super(context, container);

        this.data = this.context.employeeData || new Employee();
    }

    connectedCallback(): this {
        super.connectedCallback();

        let versionCheck = this.checkVersionAsync();

        Promise.all([versionCheck]).then(() => {
            let dataLoad = [
                this.context.store.getSeniorities(),
                this.context.params && this.context.params.assignmentId
                    ? this.context.store.getAssignment(this.context.params.assignmentId)
                    : Promise.resolve(undefined)
            ];

            Promise.all(dataLoad).then((values) => {
                this.seniorities = values[0] as string[];
                this.assignment = values[1] as AssignmentDemand;

                this.onDataLoaded();
            });
        });
        return this;
    }

    onDataLoaded() {
        $(".modal-title").html(`<i class="${ResourceDetailView.icon}"></i> Resource details`);

        let startDate = '';
        let endDate = '';

        try {
            //currently the data is taken from this field because the start date from ZEP is unreliable ex. Jenny Burger 12.11.2020 vs 31.01.2021 (which is correct)
            const startDateRaw = this.data.startDate;
            startDate = startDateRaw ? this.getHTML5DateValue(startDateRaw) : "";
            const endDateRaw = this.data.employmentPeriods[this.data.employmentPeriods.length - 1].endDate;
            endDate = endDateRaw ? this.getHTML5DateValue(endDateRaw) : "";
        } catch (error) {
            console.log(error);
        }

        const employeePhotoElement = this.data.photo
            ? createEmployeePhotoElement(this.data.photo, "employee-photo")
            : createNameIconElementWith2Initials(this.data.employeeName, "employee-photo fs-2 p-1");

        this.innerHTML = `
        <form>
            <div class="row mb-2">
                <div class="col">
                    ${employeePhotoElement}
                </div>
                <div class="col-9">
                    <label for="employeeName">Employee Name</label>                    
                    <input type="text" class="form-control" id="employeeName" value="${this.data.employeeName}" readonly>
                </div>
            </div>

            <div class="row mb-2">
                <div class="col">
                    <label for="startDate">Started</label>
                    <input type="date" class="form-control" id="startDate" value="${startDate}" readonly>
                </div>

                <div class="col ${endDate !== "" ? "" : "visually-hidden"}">
                    <label for="endDate">End</label>
                    <input type="date" class="form-control" id="endDate" value="${endDate}" readonly>
                </div>

                <div class="col">
                    <label for="weeklyHours">hours/w</label>
                    <input type="number" class="form-control" id="weeklyHours" value="${this.data.weeklyHours}" readonly>
                </div>
            </div>

            <div class="row mb-2">
                <div class="col">
                    <label for="role">Role</label>
                    <input type="text" class="form-control" id="role" list="roles" value="${this.data.role}" readonly>
                </div>

                <div class="col">
                    <label for="seniority">Seniority</label>
                    <select class="form-select" id="seniority" disabled>
                        ${this.seniorities.map((seniority) => {
            return `<option value="${seniority}" ${this.data.seniority === seniority ? "selected" : ""}>${seniority}</option>`;
        }).join("")}
                    </select>
                </div>
            </div>

            <div class="row mb-2">
                <div class="col">
                    <label for="remote">Remote</label>
                    <select class="form-select" id="remote" value="${this.data.remote}" disabled>
                        <option value="true" ${this.data.remote ? "selected" : ""}>True</option>
                        <option value="false" ${!this.data.remote ? "selected" : ""}>False</option>
                    </select>
                    </div>
                    <div class="col">
                        <label for="location">Location</label>
                        <input type="text" class="form-control" id="location" value="${this.data.location}" readonly>
                    </div>
                </div>

                <div style="display: ${this.data.internal ? 'block' : 'none'};" class="col">
                    <label for="internal">Internal Resource</label>
                    <select class="form-select" id="internal" value="${this.data.internal}" disabled>
                        <option value="true" ${this.data.internal ? "selected" : ""}>True</option>
                        <option value="false" ${!this.data.internal ? "selected" : ""}>False</option>
                    </select>
                </div>
                <div style="display: ${this.data.external ? 'block' : 'none'};" class="col">
                    <label for="external">External Resource</label>
                    <select class="form-select" id="external" value="${this.data.external}" disabled>
                        <option value="true" ${this.data.external ? "selected" : ""}>True</option>
                        <option value="false" ${!this.data.external ? "selected" : ""}>False</option>
                    </select>
                </div>
            </div>

            <div class="row mb-2">
                <div class="col">
                    <label>Leads</label>
                    (<i class="bi bi-envelope"></i>&nbsp;<a style="display: ${this.data.leads.filter(lead => lead.substring(0, 5) == "Lead-").length != 0 ? "none" : "inline"};" href="mailto:${this.data.leads[0]}@zoi.tech?bcc=${this.data.leads[1]}@zoi.tech" target="_blank">Email both leads</a>)<br>
                    ${this.data.leads.filter(lead => lead == null).length == 2 ? '' : this.leadEmailHTMLBuilder(this.data.leads[0] != null ? this.data.leads[0] : this.data.leads[1] ? this.data.leads[1] : '')}
                </div>
                <div class="col">
                    <label>&nbsp;</label><br>
                    ${this.data.leads[1] == null ? '' : this.data.leads[0] == null ? '' : this.leadEmailHTMLBuilder(this.data.leads[1])}
                </div>
            </div>

            <div class="mb-2">
                <label>Skills</label>
                <div id="skills-table"></div>
            </div>

            <div class="mb-2">
                <label>Languages</label>
                <div id="languages-table"></div>
            </div>

            <div class="alert alert-info demand-context-info" style="display:none" role="alert">
                <span></span><br/>
                <button type="button" class="btn btn-secondary btn-suggest">Suggest</button>
                <button type="button" class="btn btn-primary btn-assign">Assign</button>
            </div>

            <div class="modal-footer">
                <button type="button" class="btn btn-outline-secondary btn-findassignments"><i class="bi bi-bar-chart-steps"></i> Find assignments</button>
            </div>

        </form>`;

        const elem = $(this);

        elem.find(".btn-findassignments").click(() => {
            closeModal();
            Navigation.navigateHash("/resource-assignment/detail", { employeeId: this.data.id }, false);
        });

        if (this.assignment) {
            elem.find(".demand-context-info").show();

            const routeParamsStr = buildRouteParamsString({ assignmentId: this.assignment.id });
            elem.find(".demand-context-info span").html(`Assignment for <a href='#/resource-assignment/detail&${routeParamsStr}'>"${this.assignment.projectId}"</a> project:`);

            this.updateSuggestButtonElementByUserRole();
            this.updateAssignButtonElementByUserRole();
        }

        jQuery(() => {
            const interval = setInterval(() => {
              const tableElement = elem.find("#skills-table");
              if (tableElement.is(":visible")) {
                clearInterval(interval);
                this.addTable(); // Initialize the table only when the element is visible
              }
            }, 100); // Check every 100ms
          });

        jQuery(() => {
            const interval = setInterval(() => {
              const tableElement = elem.find("#languages-table");
              if (tableElement.is(":visible")) {
                clearInterval(interval);
                this.addLanguagesTable(); // Initialize the table only when the element is visible
              }
            }, 100);
        });
    }

    leadEmailHTMLBuilder(lead: string) {
        if (lead.substring(0, 5) == "Lead-") {
            return `<i class="bi bi-envelope"></i>&nbsp;<a target="_blank">${lead}</a>`
        }
        return `<i class="bi bi-envelope"></i>&nbsp;<a href="mailto:${lead}@zoi.tech" target="_blank">${lead}</a>`;
    }

    updateAssignButtonElementByUserRole() {
        const elem = $(this);

        const isAssigned = this.assignment.employeeId === this.data.id;
        elem.find(".btn-assign").text(isAssigned ? "Unassign" : "Assign");

        if (!this.context.user.isAdmin()) {
            elem.find(".btn-assign").hide();
            return;
        }

        elem.find(".btn-assign").click(async () => {
            if (isAssigned) {
                this.assignment.employeeId = '';
            } else {
                this.assignment.employeeId = this.data.id;
            }

            this.showLoading();
            await this.context.store.updateAssignment(this.assignment);
            this.hideLoading();
            showToast("Assignment assigned", "success");
            this.connectedCallback();
        });
    }

    updateSuggestButtonElementByUserRole() {
        const elem = $(this);

        const isSuggested = this.assignment.suggestions.includes(this.data.id);
        elem.find(".btn-suggest").text(isSuggested ? "Unsuggest" : "Suggest");

        if (!this.context.user.hasEditPermission()) {
            elem.find(".btn-suggest").hide();
            return;
        }

        elem.find(".btn-suggest").click(async () => {
            if (isSuggested) {
                this.assignment.suggestions = this.assignment.suggestions.filter((suggestion) => suggestion !== this.data.id);
            } else {
                this.assignment.suggestions.push(this.data.id);
            }

            this.showLoading();
            await this.context.store.updateAssignment(this.assignment);
            this.hideLoading();
            showToast("Assignment suggested", "success");
            this.connectedCallback();
        });
    }

    addTable() {
        const data = this.data;
        const skills = data.skills.all();

        const columns: ColumnDefinition[] = [
            {
                title: "Category",
                field: "category",
                widthGrow: 3,

                tooltip: true
            },
            {
                title: "Skill",
                field: "skill",
                widthGrow: 3,
                tooltip: true
            },
            {
                title: "Level",
                field: "level",
                formatter: "star",
                formatterParams: { stars: 4 },
                widthGrow: 1
            }
        ];

        const table = new TabulatorFull("#skills-table", {
            layout: "fitColumns",
            columns: columns,
        });

        table.on("tableBuilt", () => {
            table.setData(skills);
        });

        this.table = table;
        return this;
    }

    addLanguagesTable(elem: JQuery<HTMLElement> = $(this)) { 
        let languages = this.data.languages;
    
        const columns: ColumnDefinition[] = [
          {
            title: "Language",
            field: "language",
            widthGrow: 10,
          },
          {
            title: "Level",
            field: "level",
            widthGrow: 5,
            formatter: (cell: CellComponent) => Language.getLevelStringFromNumber(cell.getValue()),
          },
          {
            title: "Native",
            field: "isNative",
            formatter: (cell: CellComponent) => cell.getValue() ? "Yes" : "No",
            widthGrow: 5,
          },
          {
            title: "Certified",
            field: "certified",
            formatter: (cell: CellComponent) => cell.getValue() ? "Yes" : "No",
            widthGrow: 5,
          }
        ];

        const table = new TabulatorFull("#languages-table", {
          layout: "fitColumns",
          columns: columns,
        });

        table.on("tableBuilt", () => {
          table.setData(languages);
        });

        this.table = table;
        return this;
      
    }
}


/**
 * Opens the Resource Detail modal for a given employee.
 * @param {Object} employee - The employee object to display in the modal.
 * @param {PageContext} context - The page context.
 */
export function showResourceDetailModal(employee: Employee, context: PageContext) {

    context.employeeData = employee;
    $(".modal-dialog").addClass("modal-lg");
    showModal("Resource Details", ResourceDetailView, context);

    $('#detailModal').one('hidden.bs.modal', function () {
        $(".modal-dialog").removeClass("modal-lg");
    });
}


